if (!customElements.get('mca-event-ticket-options')) {
  customElements.define(
    'mca-event-ticket-options',
    class extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;
        this.form = this.closest('form');
        this.productInfo = this.closest('product-info');
        this.error = this.querySelector('[data-error]');
        this.total = this.querySelector('[data-order-total]');
        this.attendees = this.querySelector('[data-attendees]');
        this.addButton = this.querySelector('[data-add-attendee]');
        this.submitButton = this.form?.querySelector('button[type="submit"]');
        this.price = this.productInfo?.querySelector(`[id="price-${this.dataset.sectionId}"]`);
        this.installments = this.productInfo?.querySelector(`[id="product-form-installment-${this.dataset.sectionId}"]`)?.parentElement;
        if (!this.form || !this.total || !this.error || !this.attendees || !this.addButton) return;

        this.controller = new AbortController();
        this.template ??= this.rows()[0].cloneNode(true);
        this.nextAttendeeId ??= 1;
        this.prepareRow(this.rows()[0], 0);
        const options = { signal: this.controller.signal };
        this.addEventListener('change', () => this.updateSummary(), options);
        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-add-attendee]')) this.addAttendee();
          const remove = event.target.closest('[data-remove-attendee]');
          if (remove) this.removeAttendee(remove.closest('[data-attendee]'));
        }, options);
        this.form.addEventListener('submit', (event) => {
          if (!this.getSelection()) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        }, { ...options, capture: true });

        this.formatter = new Intl.NumberFormat(this.dataset.locale || undefined, {
          style: 'currency',
          currency: this.dataset.currency || 'USD',
        });
        this.productInfo?.setAttribute('data-event-group-active', '');
        this.hidden = false;
        this.updateSummary();
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.controller = null;
        this.productInfo?.removeAttribute('data-event-group-active');
      }

      rows() {
        return [...this.attendees.querySelectorAll('[data-attendee]')];
      }

      prepareRow(row, id) {
        row.querySelectorAll('input[type="radio"]').forEach((option) => {
          const label = option.nextElementSibling;
          option.id = `EventPackage-${this.dataset.sectionId}-${id}-${option.dataset.extra}`;
          option.name = `event_package_choice_${id}`;
          label.htmlFor = option.id;
        });
      }

      addAttendee() {
        if (this.rows().length >= 50) {
          this.showError(this.dataset.attendeeLimit);
          return;
        }
        const row = this.template.cloneNode(true);
        this.prepareRow(row, this.nextAttendeeId++);
        row.querySelectorAll('input[type="radio"]').forEach((option) => { option.checked = false; });
        const admission = row.querySelector('input[data-extra="0"]');
        if (admission && !admission.disabled) admission.checked = true;
        row.querySelector('[data-remove-attendee]').hidden = false;
        this.attendees.append(row);
        this.updateSummary();
        (row.querySelector('input:checked') || row.querySelector('input:not(:disabled)') || this.addButton).focus();
      }

      removeAttendee(row) {
        if (!row || row === this.rows()[0]) return;
        row.remove();
        this.updateSummary();
        this.addButton.focus();
      }

      showError(message) {
        this.error.textContent = message;
        this.error.hidden = !message;
      }

      showCartError(message) {
        this.needsCartReview = true;
        this.updateSummary();
        this.showError(`${message} ${this.dataset.reviewHint}`);
        this.querySelector('[data-review-cart]').hidden = false;
      }

      getSelection() {
        if (this.needsCartReview) {
          this.querySelector('[data-review-cart]').focus();
          return null;
        }
        const items = new Map();
        for (const row of this.rows()) {
          const option = row.querySelector('input[type="radio"]:checked');
          if (!option || option.disabled) {
            this.showError(this.dataset.unavailable);
            row.querySelector('input:not(:disabled)')?.focus();
            return null;
          }
          // Combine identical packages, never separate their admission and bracelet components.
          items.set(option.value, (items.get(option.value) || 0) + 1);
        }
        if (!items.size) return null;
        return { items: [...items].map(([id, quantity]) => ({ id, quantity })) };
      }

      updateSummary() {
        const rows = this.rows();
        let total = 0;
        let extras = 0;
        let valid = rows.length > 0;
        rows.forEach((row, index) => {
          if (index > 0) {
            row.querySelector('[data-attendee-title]').textContent = this.dataset.friendTitle.replace('__number__', index);
            row.querySelector('[data-remove-attendee]').setAttribute('aria-label', this.dataset.removeFriend.replace('__number__', index));
          }
          const option = row.querySelector('input[type="radio"]:checked');
          if (!option || option.disabled) valid = false;
          else {
            total += Number(option.dataset.price);
            extras += Number(option.dataset.extra);
          }
        });
        if (!this.needsCartReview) this.showError('');
        this.querySelector('[data-ticket-count]').textContent = rows.length;
        this.querySelector('[data-extra-count]').textContent = extras;
        this.total.textContent = valid ? this.formatter.format(total / 100) : '--';
        this.addButton.disabled = rows.length >= 50 || this.needsCartReview;
        if (this.submitButton) {
          this.submitButton.disabled = !valid || this.needsCartReview;
          const label = this.submitButton.querySelector('span');
          if (label) label.textContent = this.dataset.addToCart;
        }
        const first = rows[0]?.querySelector('input[type="radio"]:checked');
        if (first && !first.disabled) {
          this.price?.querySelectorAll('.price-item--regular, .price-item--sale').forEach((price) => {
            price.textContent = first.dataset.priceLabel;
          });
        }
        if (this.installments) this.installments.hidden = rows.length !== 1 || first?.value !== this.dataset.initialVariant;
      }
    }
  );
}
